import { useState, useEffect, useRef } from "react";
import { FaTimes, FaBars } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import logoImage from "../assets/loanInMinutes/Group.png";
import { APPLY_NOW_PATH, LOGIN_URL } from "../utils/appLinks";

const navLinks: { label: string; href: string; sectionId?: string }[] = [
  { label: "HOME", href: "/" },
  { label: "HOW IT WORKS", href: "/", sectionId: "how-it-works" },
  { label: "LOAN CALCULATOR", href: "/", sectionId: "loan-calculator" },
  { label: "REPAY", href: import.meta.env.VITE_WEBSITE_REPAY_LINK },
  { label: "ABOUT US", href: "/", sectionId: "about-us" },
  { label: "FAQ", href: "/", sectionId: "faq" },
  { label: "CONTACT", href: "/contact-us" },
]; 

const sectionIds = navLinks.map((l) => l.sectionId).filter((id): id is string => !!id);

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const lockedRef = useRef(false);
  const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") return;

    const onScroll = () => {
      if (lockedRef.current) return;

      const offset = 90; // navbar height + buffer

      // At the bottom of the page the footer may be shorter than the
      // viewport, so its top never crosses the offset threshold below.
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (atBottom) {
        setActiveSection(sectionIds[sectionIds.length - 1] ?? null);
        return;
      }

      let current: string | null = null;
      let currentTop = -Infinity;
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top - offset;
        if (top <= 0 && top > currentTop) {
          current = id;
          currentTop = top;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const scrollTo = (sectionId: string) => {
    if (location.pathname !== "/") {
      setMenuOpen(false);
      navigate(`/#${sectionId}`);
      return;
    }

    const doScroll = () => {
      const el = document.getElementById(sectionId);
      if (!el) return;

      // Pin the highlight to the clicked section immediately and ignore
      // scroll-spy updates until the smooth scroll settles — otherwise
      // sections that sit between the old and new scroll position (e.g.
      // "about-us" sits between "how-it-works" and "loan-calculator" in
      // the DOM, or "loan-calculator" sits between the top and "contact")
      // briefly flash as active mid-scroll. Long scrolls (e.g. to the
      // footer) take longer than short ones, so unlock on the actual
      // "scrollend" event rather than a fixed timer, with a generous
      // fallback timeout for browsers that don't support it.
      setActiveSection(sectionId);
      lockedRef.current = true;
      if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);

      const unlock = () => {
        lockedRef.current = false;
        if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
        window.removeEventListener("scrollend", unlock);
      };
      window.addEventListener("scrollend", unlock);
      unlockTimerRef.current = setTimeout(unlock, 2500);

      const top = el.getBoundingClientRect().top + window.scrollY - 73;
      window.scrollTo({ top, behavior: "smooth" });
    };

    if (menuOpen) {
      // mobile: close menu first, then scroll after React re-renders
      setMenuOpen(false);
      requestAnimationFrame(doScroll);
    } else {
      // desktop: scroll immediately
      doScroll();
    }
  };

  return (
    <nav className={`w-full fixed top-0 left-0 z-50 transition-colors duration-300 ${scrolled ? "bg-white shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between px-8 py-4">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img src={logoImage} alt="Loan in Seconds" className="h-15 object-contain" />
        </a>

        {/* Desktop Nav Links */}
        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((item) => {
            const isActive = item.sectionId
              ? location.pathname === "/" && activeSection === item.sectionId
              : location.pathname === item.href && !(location.pathname === "/" && activeSection !== null);
            const linkClass = `text-[11px] font-bold tracking-widest transition-colors pb-1 cursor-pointer ${
              isActive ? "text-gray-900 border-b-2 border-gray-900" : "text-gray-500 hover:text-gray-900"
            }`;
            if (item.sectionId) {
              return (
                <button key={item.label} onClick={() => scrollTo(item.sectionId!)} className={linkClass}>
                  {item.label}
                </button>
              );
            }
            return (
              <a key={item.label} href={item.href} className={linkClass}>
                {item.label}
              </a>
            );
          })}
          <a
            href={APPLY_NOW_PATH}
            className="px-6 py-2.5 bg-[#CC1A1A] text-white font-bold text-[11px] tracking-widest uppercase hover:bg-red-800 transition-colors"
          >
            APPLY NOW
          </a>
          <a
            href={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-2.5 border-2 border-[#CC1A1A] text-[#CC1A1A] font-bold text-[11px] tracking-widest uppercase hover:bg-[#CC1A1A] hover:text-white transition-colors"
          >
            LOGIN
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="lg:hidden text-2xl"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden flex flex-col items-center gap-2 pb-6 bg-white shadow-md">
          {navLinks.map((item) => {
            const mobileClass = "w-[90%] text-center px-5 py-3 text-[11px] font-bold tracking-widest text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors cursor-pointer";
            if (item.sectionId) {
              return (
                <button key={item.label} onClick={() => scrollTo(item.sectionId!)} className={mobileClass}>
                  {item.label}
                </button>
              );
            }
            return (
              <a key={item.label} href={item.href} onClick={() => setMenuOpen(false)} className={mobileClass}>
                {item.label}
              </a>
            );
          })}
          <a
            href={APPLY_NOW_PATH}
            className="w-[90%] text-center mt-2 px-5 py-3 bg-[#CC1A1A] text-white font-bold text-[11px] tracking-widest uppercase"
          >
            APPLY NOW
          </a>
          <a
            href={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-[90%] text-center mt-2 px-5 py-3 border-2 border-[#CC1A1A] text-[#CC1A1A] font-bold text-[11px] tracking-widest uppercase hover:bg-[#CC1A1A] hover:text-white transition-colors"
          >
            LOGIN
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
